package radar

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import kotlin.math.PI
import kotlin.math.hypot
import kotlin.math.min
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.random.Random
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Canvas
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import puzzlekey.Box
import puzzlekey.guideOnScreen

/**
 * The Radar's sparkles (ADR-0013 §6, docs/design.md): four-pointed stars in lavender, violet and magenta
 * on a canvas over the video. [warmth] is read every animation frame, so it can follow the Radar between
 * recompositions (and during a Scan's KDF candidates). At warmth 0 a few faint ones drift across the whole viewfinder; as it
 * rises there are more, brighter, and they fill the guide evenly. No direction hints: the score covers
 * the guide only. Sits inside the camera stage, which it covers.
 *
 * Once [gathering] turns true (the real opening, ADR-0013 §5), every star gathers into the guide's
 * rectangle over [GATHER_SECONDS] and leaves a glow there, which the LootBox comes out of (T16).
 */
@Composable
fun Sparkles(gathering: () -> Boolean = { false }, warmth: () -> Double) {
    val current by rememberUpdatedState(warmth)
    val gather by rememberUpdatedState(gathering)
    Canvas({
        classes("sparkles")
        ref { canvas ->
            val field = SparkleField(canvas, { gather() }) { current() }
            field.start()
            onDispose { field.stop() }
        }
    })
}

/** Most stars on screen, when hot. */
private const val MAX_STARS = 72

/** Stars on screen when cold. */
private const val COLD_STARS = 6

/** Seconds the shown warmth takes to catch up with the Radar, so the Scan's steps don't jump. */
private const val EASE_SECONDS = 0.8

/** How long the stars take to gather into the guide on the real opening. */
const val GATHER_SECONDS = 1.6

/** Lavender, violet, magenta, and a near-white that comes up less often. */
private val COLOURS = listOf("#c9b6ff", "#9d6bff", "#e05cff", "#c9b6ff", "#9d6bff", "#e05cff", "#f6efff")

private class Star {
    var x = 0.0
    var y = 0.0
    var size = 0.0
    var brightness = 0.0
    var sprite = 0
    var born = 0.0
    var life = 0.0
    var alive = false
    var shone = false
    var fromX = 0.0
    var fromY = 0.0
}

/** The animation behind [Sparkles]: one requestAnimationFrame loop, stars drawn from pre-rendered sprites. */
private class SparkleField(private val canvas: HTMLCanvasElement, private val gathering: () -> Boolean, private val warmth: () -> Double) {
    private val context = canvas.getContext("2d") as CanvasRenderingContext2D
    private val sprites = COLOURS.map(::sprite)
    private val stars = List(MAX_STARS) { Star() }
    private val random = Random.Default
    private var shown = 0.0
    private var last: Double? = null
    private var frame = 0
    private var gatherStart: Double? = null

    fun start() {
        frame = window.requestAnimationFrame(::draw)
    }

    fun stop() = window.cancelAnimationFrame(frame)

    private fun draw(ms: Double) {
        frame = window.requestAnimationFrame(::draw)
        val t = ms / 1000
        val dt = last?.let { t - it } ?: 0.0
        last = t
        shown += (warmth().coerceIn(0.0, 1.0) - shown) * min(1.0, dt / EASE_SECONDS)

        val ratio = window.devicePixelRatio
        val w = canvas.clientWidth.toDouble()
        val h = canvas.clientHeight.toDouble()
        if (w == 0.0 || h == 0.0) return
        val pw = (w * ratio).roundToInt()
        val ph = (h * ratio).roundToInt()
        if (canvas.width != pw || canvas.height != ph) {
            canvas.width = pw
            canvas.height = ph
        }
        context.setTransform(ratio, 0.0, 0.0, ratio, 0.0, 0.0)
        context.clearRect(0.0, 0.0, w, h)
        context.globalCompositeOperation = "lighter"

        val guide = guideOnScreen(w, h)
        if (gathering()) return gather(t, w, h, guide)
        val wanted = COLD_STARS + ((MAX_STARS - COLD_STARS) * shown).roundToInt()
        for ((i, star) in stars.withIndex()) {
            if (star.alive && t - star.born > star.life) star.alive = false
            if (!star.alive && i < wanted) spawn(star, t, w, h, guide)
            if (!star.alive) continue
            val age = (t - star.born) / star.life
            val alpha = sin(PI * age) * star.brightness
            if (alpha <= 0) continue
            val s = star.size * (0.85 + 0.15 * sin(PI * age))
            context.globalAlpha = alpha
            context.drawImage(sprites[star.sprite], star.x - s, star.y - s, 2 * s, 2 * s)
        }
        context.globalAlpha = 1.0
    }

    /**
     * The real opening: on the first frame every star (the dark ones lit inside the guide) takes off from
     * where it is, then all close in on the guide's centre and fade into a glow there. The glow at the end
     * is the one the LootBox's sky starts with (index.html, `.lootbox .sky::before`), so the handoff doesn't show.
     */
    private fun gather(t: Double, w: Double, h: Double, guide: Box) {
        val start = gatherStart ?: t.also { now ->
            gatherStart = now
            shown = 1.0
            for (star in stars) {
                if (!star.alive) spawn(star, now, w, h, guide)
                star.brightness = maxOf(star.brightness, 0.6)
                star.fromX = star.x
                star.fromY = star.y
            }
        }
        val p = ((t - start) / GATHER_SECONDS).coerceIn(0.0, 1.0)
        val e = p * p * (3 - 2 * p)
        val cx = guide.x + guide.w / 2
        val cy = guide.y + guide.h / 2

        // CSS's `radial-gradient(circle …)` reaches the farthest corner: half the diagonal.
        val glow = context.createRadialGradient(cx, cy, 0.0, cx, cy, hypot(w, h) / 2)
        glow.addColorStop(0.0, "rgba(246,239,255,${0.75 * e})")
        glow.addColorStop(0.18, "rgba(201,182,255,${0.35 * e})")
        glow.addColorStop(0.45, "rgba(157,107,255,0)")
        context.globalAlpha = 1.0
        context.fillStyle = glow
        context.fillRect(0.0, 0.0, w, h)

        for (star in stars) {
            val x = star.fromX + (cx - star.fromX) * e
            val y = star.fromY + (cy - star.fromY) * e
            val s = star.size * (1.2 - 0.7 * e)
            context.globalAlpha = star.brightness * (1 - 0.6 * e)
            context.drawImage(sprites[star.sprite], x - s, y - s, 2 * s, 2 * s)
        }
        context.globalAlpha = 1.0
    }

    /**
     * A new star: inside the guide with probability [shown], else anywhere in view. Of a few random
     * places it takes the one farthest from the other stars, so they fill the area evenly.
     */
    private fun spawn(star: Star, t: Double, w: Double, h: Double, guide: Box) {
        val area = if (random.nextDouble() < shown) guide else Box(0.0, 0.0, w, h)
        var bestX = 0.0
        var bestY = 0.0
        var bestGap = -1.0
        repeat(4) {
            val x = area.x + random.nextDouble() * area.w
            val y = area.y + random.nextDouble() * area.h
            var gap = Double.MAX_VALUE
            for (o in stars) if (o.alive) gap = min(gap, (o.x - x) * (o.x - x) + (o.y - y) * (o.y - y))
            if (gap > bestGap) {
                bestGap = gap
                bestX = x
                bestY = y
            }
        }
        star.x = bestX
        star.y = bestY
        star.size = (6 + 10 * shown) * (0.6 + 0.7 * random.nextDouble())
        star.brightness = (0.25 + 0.75 * shown) * (0.6 + 0.4 * random.nextDouble())
        star.sprite = random.nextInt(sprites.size)
        star.life = 1.0 + 1.4 * random.nextDouble()
        // A star's first life starts part-way through, so the field doesn't pulse in step.
        star.born = if (star.shone) t else t - random.nextDouble() * star.life * 0.5
        star.alive = true
        star.shone = true
    }
}

/** Sprite pixels, square: the star's tips reach almost to the edge, the glow fills the square. */
private const val SPRITE = 96

/** One star in [colour] with a soft glow, drawn once and then scaled per star. */
private fun sprite(colour: String): HTMLCanvasElement {
    val canvas = (document.createElement("canvas") as HTMLCanvasElement).apply {
        width = SPRITE
        height = SPRITE
    }
    val c = canvas.getContext("2d") as CanvasRenderingContext2D
    val mid = SPRITE / 2.0
    val glow = c.createRadialGradient(mid, mid, 0.0, mid, mid, mid)
    glow.addColorStop(0.0, colour + "99")
    glow.addColorStop(0.35, colour + "33")
    glow.addColorStop(1.0, colour + "00")
    c.fillStyle = glow
    c.fillRect(0.0, 0.0, SPRITE.toDouble(), SPRITE.toDouble())

    // Four points, concave sides: a quadratic curve through the centre between each pair of tips.
    val r = SPRITE * 0.45
    val pinch = r * 0.12
    c.beginPath()
    c.moveTo(mid, mid - r)
    c.quadraticCurveTo(mid + pinch, mid - pinch, mid + r, mid)
    c.quadraticCurveTo(mid + pinch, mid + pinch, mid, mid + r)
    c.quadraticCurveTo(mid - pinch, mid + pinch, mid - r, mid)
    c.quadraticCurveTo(mid - pinch, mid - pinch, mid, mid - r)
    c.closePath()
    c.fillStyle = colour
    c.fill()
    val core = c.createRadialGradient(mid, mid, 0.0, mid, mid, r * 0.25)
    core.addColorStop(0.0, "#ffffffff")
    core.addColorStop(1.0, "#ffffff00")
    c.fillStyle = core
    c.fillRect(mid - r, mid - r, 2 * r, 2 * r)
    return canvas
}
